import { useContext, useEffect, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap"; // Import Dropdown
import { UserContext } from "../user/UserContext";
import SidebarNavItem from "./SidebarNavItem";
import BottombarNavItem from "./BottombarNavItem";
import UserDropdown from "./UserDropdown";
import Search from "../Search"; // Import the Search component
import { setActiveItem } from "../../redux/slices/sidebarSlice";
import { BookHalf, Search as SearchIcon } from "react-bootstrap-icons"; // Import icons

export default function Sidebar({ navItems = [], signOut }) {
  const dispatch = useDispatch(); // Initialize the Redux dispatch function
  const navigate = useNavigate(); // Initialize useNavigate for programmatic navigation
  const { userAttributes, clearUserAttributes } = useContext(UserContext); // Access user attributes and clearUserAttributes function from context
  const [isSearchVisible, setIsSearchVisible] = useState(false); // State to control the visibility of the Search component
  const searchRef = useRef(null); // Create a ref to track the Search component
  const [dropdownOpen, setDropdownOpen] = useState(false); // State to control the visibility of the dropdown

  // Access activeItem state from Redux store
  const activeItem = useSelector((state) => state.sidebar.activeItem);

  // Filter out the Vocabulary item from the navItems array
  const filteredNavItems = navItems.filter(
    (item) => item.name !== "Vocabulary"
  );

  // Example submenu items for "Exercises"
  const exerciseSubMenu = [
    // {
    //   name: "Grammatical Exercises",
    //   path: "/exercises/grammar",
    //   icon: BookHalf,
    // },
    {
      name: "Vocabulary Exercises",
      path: "/exercises/vocabulary",
      icon: BookHalf,
    },
    { name: "Reading", path: "/exercises/reading", icon: BookHalf },
    // { name: "Listening", path: "/exercises/listening", icon: BookHalf },
  ];

  // useEffect to set the active item based on the current path
  useEffect(() => {
    const currentPath = window.location.pathname; // Get the current path from the window object
    const activeNavItem = filteredNavItems.find((item) =>
      currentPath.includes(item.path)
    );
    if (activeNavItem) {
      dispatch(setActiveItem(activeNavItem.name)); // Dispatch action to set active item
    } else {
      dispatch(setActiveItem("Home")); // Default to "Home" if no match is found
    }
  }, [dispatch, filteredNavItems]);

  // Close Search when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchVisible(false); // Close the Search component
      }
    }

    if (isSearchVisible) {
      // Add event listener to detect clicks outside of the search component
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      // Cleanup the event listener when the component unmounts or visibility changes
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchVisible]);

  // useCallback to handle navigation item selection
  const handleSelect = useCallback(
    (item) => {
      dispatch(setActiveItem(item.name)); // Dispatch action to set active item
      navigate(item.path); // Navigate to the selected item's path
    },
    [dispatch, navigate]
  );

  // Toggle the visibility of the Search component
  const handleSearchClick = () => {
    setIsSearchVisible((prev) => !prev);
  };

  // Function to handle user sign out
  const handleSignOut = async () => {
    try {
      clearUserAttributes(); // Clear user attributes from context
      await signOut(); // Call signOut function passed as a prop
      navigate("/signin"); // Navigate to the sign-in page
    } catch (error) {
      console.log("error signing out:", error); // Log any errors during sign out
    }
  };

  return (
    <div>
      {/* Sidebar for desktop screens */}
      <div
        className="d-flex flex-column flex-shrink-0 p-3 bg-body-tertiary vh-100 d-none d-md-flex"
        style={{ width: "280px" }}
      >
        <a
          href="/"
          className="d-flex align-items-center mb-3 mb-md-0 me-md-auto text-decoration-none"
        >
          <BookHalf width="40" height="40" /> {/* Sidebar header icon */}
          <span className="fs-4 ms-4">Fluent Serbian</span>{" "}
          {/* Sidebar header text */}
        </a>
        <hr />
        {/* Navigation items */}
        <ul className="nav nav-pills flex-column" style={{ flexShrink: 0 }}>
          {filteredNavItems.map((item) =>
            item.name === "Exercises" ? (
              <Dropdown
                key={item.name}
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
                //onClick={() => handleSelect(item)}
                show={dropdownOpen}
                drop="end"
              >
                {/* Pass isDropdown={true} to ensure we render the custom Dropdown.Toggle */}
                <SidebarNavItem item={{ ...item }} isDropdown={true} />

                <Dropdown.Menu>
                  {exerciseSubMenu.map((subItem, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => handleSelect(subItem)}
                      className="d-flex align-items-center"
                    >
                      <BookHalf className="me-2" /> {/* Sub-item icon */}
                      {subItem.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={activeItem === item.name}
                onSelect={() => handleSelect(item)}
              />
            )
          )}
        </ul>

        <hr />
        {/* Search section */}
        <ul className="nav nav-pills flex-column mb-auto">
          <li
            className="nav-item"
            style={{ marginBottom: "5px", paddingLeft: "1rem" }}
          >
            Search Vocabulary
          </li>
          <li className="nav-item" style={{ marginBottom: "5px" }}>
            <Search /> {/* Render the Search component */}
          </li>
        </ul>
        <hr />
        {/* User dropdown for account-related actions */}
        <UserDropdown
          userAttributes={userAttributes}
          onSignOut={handleSignOut}
        />
      </div>

      {/* Bottom bar for mobile screens */}
      <div id="bottom-bar" className="d-md-none fixed-bottom bg-body-tertiary">
        <div ref={searchRef}>
          {isSearchVisible && <Search />}{" "}
          {/* Render the Search component if visible */}
        </div>
        <ul className="nav justify-content-around">
          {filteredNavItems.map((item) =>
            item.name === "Exercises" ? (
              <Dropdown
                key={item.name}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                show={dropdownOpen}
                drop="up" // Ensures that the dropdown opens upwards on mobile screens
              >
                {/* Pass isDropdown={true} to ensure we render the custom Dropdown.Toggle */}
                <BottombarNavItem
                  key={item.name}
                  item={{ ...item }}
                  isDropdown={true}
                />
                <Dropdown.Menu>
                  {exerciseSubMenu.map((subItem, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => handleSelect(subItem)}
                      className="d-flex align-items-center"
                    >
                      <BookHalf className="me-2" /> {/* Sub-item icon */}
                      {subItem.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <BottombarNavItem
                key={item.name}
                item={item}
                onSelect={() => handleSelect(item)}
              />
            )
          )}

          <li>
            <button
              type="button"
              className="nav-link btn btn-link text-start w-100 link-body-emphasis"
              aria-current="page"
              style={{
                border: "none",
                background: "none",
                padding: "0.5rem 1rem",
                marginBottom: "5px",
                borderRadius: "0.375rem",
              }}
              aria-label={"Navigate to Search"}
              onClick={handleSearchClick}
            >
              <SearchIcon width={24} height={24} className="bi pe-none me-2" />
            </button>
          </li>

          {/* User dropdown for mobile view */}
          <UserDropdown
            userAttributes={userAttributes}
            onSignOut={handleSignOut}
          />
        </ul>
      </div>
    </div>
  );
}
